import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// react-bootstrap
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';
import Stack from 'react-bootstrap/Stack';
import Image from 'react-bootstrap/Image';
import Button from 'react-bootstrap/Button';

// project-imports
import useConfig from 'hooks/useConfig';
import { ThemeMode } from 'config';
import { setResolvedTheme } from 'components/setResolvedTheme';
import useAuth from '../../auth/useAuth';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import MainCard from '../../components/MainCard';
import SimpleBarScroll from 'components/third-party/SimpleBar';
// assets fallback avatar
import DefaultAvatar from 'assets/images/user/avatar-2.png';

import { getPusher, channels } from 'utils/pusher';

import { useGetNotifications, useReadAllNotifications, useReadNotification } from '../../APIs/Notification/Notification';
import { notify } from '@common/NotificationService';

export default function Header() {
  const { onChangeMode, mode, modePreference } = useConfig();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const { notifications: apiNotifications } = useGetNotifications();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const { readAll } = useReadAllNotifications();
  const { readOne } = useReadNotification();
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    setResolvedTheme(mode);
  }, [mode]);

  useEffect(() => {
    if (!apiNotifications.length) return;

    const unread = apiNotifications.filter((n) => !n.is_read);

    setNotifications(unread);
    setUnreadCount(unread.length);
  }, [apiNotifications.length]);

  useEffect(() => {
    if (!user?.user?.id) return;

    const pusher = getPusher();
    const channelName = channels.user(user.user.id);
    const channel = pusher.subscribe(channelName);

    const handleEvent = (payload) => {
      setNotifications((prev) => {
        // prevent duplicates
        if (prev.some((n) => n.id === payload.id)) return prev;
        return [payload, ...prev];
      });

      setUnreadCount((prev) => prev + 1);
    };

    channel.bind('booking.notification', handleEvent);

    return () => {
      channel.unbind('booking.notification', handleEvent);
      pusher.unsubscribe(channelName);
    };
  }, [user?.user?.id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRead = async (id) => {
    await readOne(id);

    setNotifications((prev) => prev.filter((n) => n.id !== id));

    setUnreadCount((prev) => Math.max(prev - 1, 0));
  };

  const handleNotificationClick = async (notification) => {
    try {
      await readOne(notification.id);
    } catch (e) {
      // ignore read failure for UX
    }

    // remove from unread dropdown
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    setUnreadCount((prev) => Math.max(prev - 1, 0));
    setShowNotifications(false);

    // redirect for booking-related notifications
    if (notification.type?.toLowerCase().startsWith('booking')) {
      navigate('/booking-management', {
        state: {
          bookingId: notification.booking_id,
          openView: true
        }
      });
    } else if (notification.type?.toLowerCase().startsWith('giftcard')) {
      navigate('./gift_card');
    } else if (notification.type?.toLowerCase().startsWith('stock_request')) {
      // Straight to the request it is about. The store room reads ?tab= and
      // ?request= on open, so this lands on the Requests tab with that one
      // already showing rather than on a list to search through.
      const id = notification.data?.stock_request_id;
      navigate(`/store-room?tab=requests${id ? `&request=${id}` : ''}`);
    }
  };

  const handleReadAll = async (e) => {
    e.preventDefault();

    try {
      const res = await readAll();

      if (res?.success) {
        setNotifications([]);
        setUnreadCount(0);
      } else {
        notify.error(res?.message || 'Failed to mark notifications as read', '');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Something went wrong while marking notifications as read';

      notify.error(errorMessage, '');
    }
  };

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  return (
    <header className="pc-header">
      <div className="header-wrapper">
        {/* LEFT SECTION - SIDEBAR TOGGLE */}

        <div className="me-auto pc-mob-drp">
          <Nav className="list-unstyled">
            {/* DESKTOP SIDEBAR TOGGLE */}
            <Nav.Item className="pc-h-item pc-sidebar-collapse">
              <Nav.Link
                as={Link}
                to="#"
                className="pc-head-link ms-0"
                id="sidebar-hide"
                onClick={() => {
                  handlerDrawerOpen(!drawerOpen);
                }}
              >
                <i className="ph ph-list" />
              </Nav.Link>
            </Nav.Item>

            {/* MOBILE SIDEBAR TOGGLE (Fix) */}
            <Nav.Item className="pc-h-item pc-sidebar-popup">
              <Nav.Link as={Link} to="#" className="pc-head-link ms-0" id="mobile-collapse" onClick={() => handlerDrawerOpen(!drawerOpen)}>
                <i className="ph ph-list" />
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {/* RIGHT SECTION */}
        <div className="ms-auto">
          <Nav className="list-unstyled">
            {/* <Dropdown className="pc-h-item" align="end"> */}
            <Dropdown className="pc-h-item" align="end" show={showNotifications} onToggle={(isOpen) => setShowNotifications(isOpen)}>
              <Dropdown.Toggle className="pc-head-link me-0 arrow-none" variant="link" id="notification-dropdown">
                <i className="ph ph-bell" />
                {/* <span className="badge bg-success pc-h-badge">{unreadCount}</span> */}
                <span className="badge bg-success pc-h-badge text-black">{unreadCount}</span>
              </Dropdown.Toggle>

              {/* A panel sized for a list of notifications looks abandoned when
                  it holds one line, so the empty case gets its own smaller box. */}
              <Dropdown.Menu
                className={`dropdown-notification pc-h-dropdown${notifications.length === 0 ? ' dropdown-notification-empty' : ''}`}
              >
                <Dropdown.Header className="d-flex align-items-center justify-content-between">
                  <h5 className="m-0">Notifications</h5>
                  {/* Nothing unread to act on, so nothing to offer. */}
                  {notifications.length > 0 && (
                    <Link className="btn btn-link btn-sm" onClick={handleReadAll}>
                      Mark all read
                    </Link>
                  )}

                  {/* <Link to="/notifications" className="btn btn-link btn-sm">
                    View all
                  </Link> */}
                  <Link to="/notifications" className="btn btn-link btn-sm" onClick={() => setShowNotifications(false)}>
                    View all
                  </Link>
                </Dropdown.Header>
                <SimpleBarScroll style={{ maxHeight: 'calc(100vh - 215px)' }}>
                  <div className="dropdown-body text-wrap position-relative">
                    {/* Nothing to show is worth saying: an empty panel under a
                        header reads as something that failed to load. */}
                    {notifications.length === 0 && (
                      <div className="pc-notification-empty">
                        <i className="ph ph-bell-slash" />
                        <span>- No new notifications -</span>
                      </div>
                    )}

                    {notifications.map((notification) => (
                      <MainCard key={notification?.id} className="mb-0">
                        <Stack direction="horizontal" gap={3}>
                          <Image src={DefaultAvatar} className="img-radius avatar rounded-0" />

                          <div style={{ cursor: 'pointer' }} onClick={() => handleNotificationClick(notification)}>
                            <span className="float-end text-sm text-muted">{notification?.created_at}</span>

                            <h5 className="mb-2">{notification?.title}</h5>

                            <p className="mb-0">{notification?.message}</p>
                          </div>
                        </Stack>
                      </MainCard>
                    ))}
                  </div>
                </SimpleBarScroll>
              </Dropdown.Menu>
            </Dropdown>
            {/* THEME SWITCHER */}
            <Dropdown className="pc-h-item" align="end">
              <Dropdown.Toggle className="pc-head-link me-0 arrow-none" variant="link">
                <i className="ph ph-sun-dim" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="pc-h-dropdown">
                <Dropdown.Item onClick={() => onChangeMode(ThemeMode.DARK)} className="d-flex align-items-center justify-content-between">
                  <span>
                    <i className="ph ph-moon" /> Dark
                  </span>
                  {modePreference === ThemeMode.DARK && <i className="ph ph-check ms-3 text-primary" />}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onChangeMode(ThemeMode.LIGHT)} className="d-flex align-items-center justify-content-between">
                  <span>
                    <i className="ph ph-sun" /> Light
                  </span>
                  {modePreference === ThemeMode.LIGHT && <i className="ph ph-check ms-3 text-primary" />}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onChangeMode(ThemeMode.AUTO)} className="d-flex align-items-center justify-content-between">
                  <span>
                    <i className="ph ph-cpu" /> Default
                  </span>
                  {modePreference === ThemeMode.AUTO && <i className="ph ph-check ms-3 text-primary" />}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* USER PROFILE DROPDOWN */}
            <Dropdown className="pc-h-item" align="end">
              <Dropdown.Toggle className="pc-head-link arrow-none me-0" variant="link" id="user-profile-dropdown">
                <i className="ph ph-user-circle" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-user-profile pc-h-dropdown p-0 overflow-hidden">
                {/* PROFILE HEADER */}
                <Dropdown.Header className="bg-primary">
                  <Stack direction="horizontal" gap={3} className="my-2">
                    <Image src={user?.user?.avatar || DefaultAvatar} alt="user-avatar" className="user-avatar wid-35" roundedCircle />
                    <Stack gap={1}>
                      <h6 className="text-white mb-0">{user?.user?.name || 'Guest User'}</h6>
                      <span className="text-white text-opacity-75">{user?.user?.email || 'no-email'}</span>
                    </Stack>
                  </Stack>
                </Dropdown.Header>

                {/* PROFILE MENU */}
                <div className="dropdown-body">
                  <div className="profile-notification-scroll position-relative" style={{ maxHeight: 'calc(100vh - 225px)' }}>
                    <Dropdown.Item as={Link} to="/settings" className="justify-content-start">
                      <i className="ph ph-gear me-2" /> Settings
                    </Dropdown.Item>

                    {/* LOGOUT BUTTON */}
                    <div className="d-grid my-2 px-3">
                      <Button onClick={handleLogout} variant="danger">
                        <i className="ph ph-sign-out me-2" />
                        Logout
                      </Button>
                    </div>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </div>
      </div>
    </header>
  );
}
